/**
 * CUB Design System — Brand Tokens
 * ─────────────────────────────────
 * Fonte de verdade para cores, tipografia e forma da marca CUB.
 * Importe este arquivo em qualquer parte do produto — componentes,
 * gráficos, utilitários — sem depender do objeto `cubTheme` inteiro.
 *
 * Usage:
 *   import { tokens } from "@/theme/tokens";
 *   <Chip sx={{ bgcolor: tokens.chartreuse, color: tokens.midnight }} />
 */

export const tokens = {
  // ─── Paleta principal ───────────────────────────────────────────────
  /** Crayola Blue (Pantone 2727 C) — cor primária da marca */
  blue: "#417DFA",
  blueHover: "#3466CE",
  blueLight: "#6696FA",
  blue100: "#C6D8FE",

  /** True Cobalt (Pantone 661 C) — azul secundário / estrutural */
  cobalt: "#042E87",

  /** Chartreuse (Pantone 374 C) — destaque / CTA / atenção */
  chartreuse: "#D7F958",
  /** Forest Moss — suporte ao chartreuse, ênfase */
  forestMoss: "#739100",

  /** Midnight Violet (Pantone 2758 C) — neutro profundo */
  midnight: "#1C0D26",
  /** Alice Blue (Pantone 656 C) — neutro claro */
  aliceBlue: "#DFEAF6",

  // ─── Exceções de acessibilidade ──────────────────────────────────────
  // NÃO são cores de marca novas — são ajustes pontuais de uma cor da
  // paleta principal para cumprir contraste AA num contexto específico.
  /**
   * `blue` (#417DFA) com texto branco 14px reprova AA (3,79:1). Esta
   * variante passa (4,85:1) — usada como superfície da sidebar do preset
   * `recebiveis` (ver `Sidebar/productThemes.ts`). Decisão registrada em
   * jul/2026 (squad); não usar fora desse contexto sem nova análise.
   */
  blueAA: "#1F66F9",

  // ─── Escala de neutros (blue-tinted) ────────────────────────────────
  // Paleta de apoio — uso pontual quando o designer pede um tom azulado
  // intencional (boards de marca, headers temáticos, etc.). Para superfícies
  // e UI gerais, prefira a escala `grey*` abaixo.
  neutral50: "#E1E6F0",
  neutral100: "#DFEAF6",
  neutral200: "#CDD4E2",
  neutral300: "#C2C7CD",
  neutral400: "#8693AD",
  neutral500: "#707C94",
  neutral600: "#555F74",
  neutral700: "#323948",
  neutral800: "#1B1F27",
  neutral900: "#1C0D26",

  // ─── Escala de cinzas (puros, Material Design) ──────────────────────
  // Padrão para superfícies, bordas, divisores, texto secundário e UI em geral.
  // É a escala consumida pelo `cubTheme` (palette.grey, inputs, switches,
  // outlined borders) e pelos tokens semânticos `bg*` / `text*` deste arquivo.
  grey50:  "#FAFAFA",
  grey100: "#F5F5F5",
  grey200: "#EEEEEE",
  grey300: "#E0E0E0",
  grey400: "#BDBDBD",
  grey500: "#9E9E9E",
  grey600: "#757575",
  grey700: "#616161",
  grey800: "#424242",
  grey900: "#212121",

  // ─── Semânticas ──────────────────────────────────────────────────────
  // Escala de 5 stops: 100 (light) · 200 · 300 (main) · 400 · 500 (dark)
  success: "#00B42A",
  success100: "#E8FFEA",
  success200: "#7BE188",
  success300: "#00B42A",
  success400: "#007D22",
  success500: "#005C1A",

  // Escala amber — afasta do error (vermelho) e mantém AA com texto branco em `warning`.
  warning: "#F59E0B",
  warning100: "#FFFBEB",
  warning200: "#FDE68A",
  warning300: "#F59E0B",
  warning400: "#D97706",
  warning500: "#92400E",

  error: "#F53F3F",
  error100: "#FFECE8",
  error200: "#FBACA3",
  error300: "#F53F3F",
  error400: "#CB2634",
  error500: "#A61F2D",

  // Escala blue — `info` usa o azul CUB (#417DFA) como main (300).
  info: "#417DFA",
  info100: "#EBF1FE",
  info200: "#93B5FD",
  info300: "#417DFA",
  info400: "#3466CE",
  info500: "#1D48A8",

  // Escala violet — `pending` ecoa `midnight` (Midnight Violet) e separa de warning/error.
  // Convenção universal de "transação em processamento" em fintech.
  pending: "#8B5CF6",
  pending100: "#F5F3FF",
  pending200: "#DDD6FE",
  pending300: "#8B5CF6",
  pending400: "#5B21B6",
  pending500: "#3B1494",

  // ─── Texto ───────────────────────────────────────────────────────────
  textPrimary: "#1B1B1C",
  textSecondary: "#616161", // = grey700
  textMuted: "#9E9E9E",     // = grey500
  /** Texto branco — para contrastText sobre cores de marca e estado disabled */
  white: "#FFFFFF",
  /** Preto puro — base para scrims/véus com alpha sobre superfícies de marca (ex.: Sidebar) */
  black: "#000000",
  /** Cor de texto em estado disabled (buttons, inputs, labels) */
  disabledText: "#8C8B8B",

  // ─── Superfícies ─────────────────────────────────────────────────────
  bgDefault: "#FFFFFF",
  bgMuted: "#F5F5F5", // = grey100
  bgBoard: "#FAFAFA", // = grey50
  bgDark: "#1C0D26",  // = midnight (brand)

  // ─── Bordas e divisores ──────────────────────────────────────────────
  border: "rgba(0,0,0,0.12)",
  borderStrong: "rgba(0,0,0,0.23)",
  divider: "rgba(0,0,0,0.2)",

  // ─── Sombras (MUI elevation) ─────────────────────────────────────────
  shadow1: "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
  shadow2: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
  shadow4: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
  shadow8: "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",

  // ─── Tipografia ──────────────────────────────────────────────────────
  /** Display — Commuters Sans (ou Montserrat como fallback) */
  fontDisplay: '"Commuters Sans", "Montserrat", system-ui, sans-serif',
  /** Body — Inter */
  fontBody: '"Inter", "Helvetica Neue", Arial, sans-serif',
  /** Mono — Roboto Mono */
  fontMono: '"Roboto Mono", ui-monospace, Menlo, monospace',

  // ─── Radii ───────────────────────────────────────────────────────────
  // Escala numérica (em px, segue padrão MUI shape × multiplicador)
  radiusXs: "4px",
  radiusSm: "6px",
  radiusMd: "8px",    // ← shape.borderRadius (base MUI) — input, button
  radiusLg: "12px",   // ← Paper, Alert
  radiusXl: "16px",   // ← Card
  radiusXxl: "20px",   // ← brand boards
  radius3xl: "40px",
  radiusFull: "999px",  // ← Chip / pill

  // Aliases semânticos (use estes nos overrides do tema sempre que possível)
  radiusButton: "4px",  // CUB spec — botões com raio mais apertado
  radiusInput: "4px",
  radiusPaper: "8px",
  radiusAlert: "8px",
  radiusCard: "8px",
  radiusPill: "999px",

  // ─── Font Sizes ──────────────────────────────────────────────────────
  fontSize11: 11,
  fontSize12: 12, // Body XS / Caption
  fontSize14: 14, // Body S / Button S
  fontSize16: 16, // Body / Button
  fontSize18: 18, // Button L
  fontSize20: 20, // H3
  fontSize24: 24, // H2
  fontSize32: 32, // H1
  fontSize40: 40, // Display
  fontSize48: 48, // Display L
} as const;

export type Tokens = typeof tokens;
export default tokens;
